import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Terminal } from 'lucide-react';

const NotFound = () => {
  const navigate = useNavigate();

  return (
    <section
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: 'var(--bg-primary)' }}
    >
      {/* Grid background */}
      <div className="absolute inset-0 grid-bg opacity-40" />

      {/* Radial glow */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 50% 40% at 50% 40%, var(--accent-glow) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <div className="container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-center max-w-lg mx-auto"
        >
          {/* Glitchy 404 */}
          <motion.div
            className="mb-8"
            animate={{
              textShadow: [
                '0 0 0 transparent',
                '2px 0 rgba(209, 0, 163, 0.5), -2px 0 rgba(124, 58, 237, 0.5)',
                '0 0 0 transparent',
              ],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              repeatType: 'loop',
            }}
          >
            <span
              className="text-[8rem] sm:text-[10rem] font-black leading-none tracking-tighter"
              style={{
                background: 'var(--gradient-accent)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              404
            </span>
          </motion.div>

          {/* Terminal-style message */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mb-8 p-5 rounded-xl text-left"
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Terminal
                className="w-4 h-4"
                style={{ color: 'var(--accent)' }}
              />
              <span
                className="text-mono text-xs"
                style={{ color: 'var(--text-muted)' }}
              >
                feral@techlabs ~
              </span>
            </div>
            <div className="text-mono text-sm space-y-1">
              <p style={{ color: 'var(--text-secondary)' }}>
                <span style={{ color: 'var(--accent-light)' }}>$</span> find /
                -name "this-page"
              </p>
              <p style={{ color: 'var(--text-muted)' }}>
                Error: No matches found
              </p>
              <p style={{ color: 'var(--text-secondary)' }}>
                <span style={{ color: 'var(--accent-light)' }}>$</span>{' '}
                <span className="animate-pulse">▊</span>
              </p>
            </div>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-title mb-3"
          >
            Page not found.
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-body mb-8"
            style={{ color: 'var(--text-tertiary)' }}
          >
            Looks like this route doesn't exist. Maybe it was moved, or maybe
            you just typed something creative. Either way, let's get you back on
            track.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <button
              onClick={() => navigate('/')}
              className="btn-primary btn-fill-hover"
            >
              <span className="flex items-center gap-2 relative z-10">
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </span>
            </button>
            <button
              onClick={() => navigate('/blog')}
              className="btn-secondary"
            >
              Read the Blog
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default NotFound;
